import { Fragment, useContext, useEffect, useState } from 'react';
import { addTape, deleteTape, updateTape, tapeGeometryDisplay, tapeGeometryOptions, tapeSourceDisplay, tapeSourceOptions } from '../services/tapeService';
import { Button } from 'react-bootstrap'
import ProtectedComponent from './common/ProtectedComponent';
import { AuthContext } from './Login/AuthContext';
import Icons from './common/Icons';

import { DatabaseContext } from "./DatabaseContext"
import { dateYYYYMMMDD, validateAlphanumeric, validateNonNegativeInt, validatePositiveInt } from '../services/commonParse';
import TableViewAdd, { defaultAdd, defaultResetAdd } from './common/TableViewAdd';
import TableViewEdit, { defaultReset, defaultEdit, defaultValidateEdit } from './common/TableViewEdit';
import { HGCFormRowText, HGCFormRowDate, HGCFormRowSelect } from './common/SimpleForm';
import { userOptions } from '../services/userService';
import PaginateTable, { CategoryFilter, FuzzyTextFilter, PassFailFilter } from './common/TableDisplay';
import { findTableRow } from '../services/tableUtils';
import { SimpleFold, GeometryCard } from './common/misc';

import FL0 from "../images/tape/FL0.png"
import FH0 from "../images/tape/FH0.png"
import Five from "../images/tape/5L0.png"
import BL0 from "../images/tape/BL0.png"
import TL0 from "../images/tape/TL0.png"
import LL0 from "../images/tape/LL0.png"
import RL0 from "../images/tape/RL0.png"
import THT from "../images/tape/THT.png"
import BHW from "../images/tape/BHW.png"
import LHW from "../images/tape/LHW.png"
import RHW from "../images/tape/RHW.png"

const TapeColumns = () => [
  { Header: 'Batch ID', Cell: TapeCellID, Filter: FuzzyTextFilter((tape) => [tape.batch_id]) },
  { Header: 'Manufacturer', Cell: (tape) => tapeSourceDisplay(tape.source), Filter: CategoryFilter((tape) => tape.source) },
  { Header: 'Geometry', Cell: TapeCellType, Filter: CategoryFilter((tape) => tape.type) },
  { Header: 'Available', Cell: TapeCellAvailable, Filter: PassFailFilter((tape) => tape.available > 0, "Hide empty", true) },
  { Header: 'Used', Cell: (tape) => tape.used_count },
  { Header: 'Delivery total', Cell: (tape) => tape.delivery_count },
  { Header: 'Defective count', Cell: (tape) => tape.defective_count },
  { Header: 'Date (CEST)', Cell: (tape) => <div>Rec/Exp | {dateYYYYMMMDD(tape.batch_reception_date)} / {dateYYYYMMMDD(tape.expiry_date)}</div> },
  { Header: 'Comment', Cell: (tape) => tape.comment },
]

const TapeCellID = (tape) => {
  const { user } = useContext(AuthContext);
  const [showEdit, setShowEdit] = useState(false);
  const { updateTapeList } = useContext(DatabaseContext);
  const removeAction = async () => {
    await deleteTape(tape.id);
    updateTapeList();
  }

  return <Fragment>
    {tape.batch_id}
    <Button variant='light' size='sm' onClick={() => { setShowEdit(true) }}>{Icons.edit}</Button>
    {user.role === "admin" && <Button variant='light' size='sm' onClick={removeAction}>{Icons.remove}</Button>}
    {showEdit && <TapeEdit id={tape.id} showState={[showEdit, setShowEdit]} />}
  </Fragment>
}

const TapeCellType = (tape) => {
  return <Fragment>{tapeGeometryDisplay(tape.type)} v{tape.version} ({tape.type})</Fragment>
}

const TapeCellAvailable = (tape) => {
  const available = tape.available;
  return <Fragment>{available <= 0 ? <b>{available}</b> : available}</Fragment>
}

const TapeEdit = ({ id, showState }) => {
  const [, setShow] = showState;
  const { tapeList, updateTapeList, userList } = useContext(DatabaseContext);
  const origData = findTableRow(tapeList, (x) => x.id === id);
  // Creating all items related to the display form
  const form = {
    'batch_id': useState(origData.batch_id),
    'batch_reception_date': useState(origData.batch_reception_date),
    'source': useState(origData.source),
    "receiver": useState(origData.receiver),
    "expiry_date": useState(origData.expiry_date),
    "type": useState(origData.type),
    "version": useState(origData.version),
    "delivery_count": useState(origData.delivery_count),
    "defective_count": useState(origData.defective_count),
  }
  // eslint-disable-next-line
  const errorForm = {
    "batch_id": useState(""),
    'source': useState(""),
    "type": useState(""),
    "version": useState(""),
    "delivery_count": useState(""),
    "defective_count": useState("")
  };
  const [invalid, setInvalid] = useState("")

  const resetAction = defaultReset(form, origData)
  const editAction = defaultEdit(id, form, updateTape, updateTapeList, setShow)
  useEffect(() => { setInvalid(defaultValidateEdit(errorForm)) }, [errorForm])

  return <TableViewEdit title={`Edit Tape order [${id}][${origData.batch_id}]`} showState={showState} resetAction={resetAction} editAction={editAction} invalid={invalid}>
    <HGCFormRowDate label="Batch Reception Date" dataState={form["batch_reception_date"]} />
    <HGCFormRowSelect label="Reciever" dataState={form["source"]} errorState={errorForm["source"]} options={tapeSourceOptions} allowNull={false} />
    <HGCFormRowSelect label="Reciever" dataState={form["receiver"]} options={userOptions(userList)} />
    <HGCFormRowDate label="Expiry Date" dataState={form["expiry_date"]} />
    <HGCFormRowSelect label="Geometry Type" dataState={form["type"]} errorState={errorForm["type"]} options={tapeGeometryOptions} allowNull={false} />
    <HGCFormRowText label="Version" dataState={form["version"]} errorState={errorForm["version"]} validate={validatePositiveInt()} />
    <HGCFormRowText label="Delivery count" dataState={form["delivery_count"]} errorState={errorForm["delivery_count"]} validate={validatePositiveInt(true)} />
    <HGCFormRowText label="Defective count" dataState={form["defective_count"]} errorState={errorForm["defective_count"]} validate={validateNonNegativeInt()} />
  </TableViewEdit>
}

const GeometryReference = () => {
  return <>
    <SimpleFold header='Tape Geometry Reference (backing paper up)'>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        <GeometryCard src={FL0} bodytext={"FL0 (LD Full)"} />
        <GeometryCard src={FH0} bodytext={"FH0 (HD Full)"} />
        <GeometryCard src={Five} bodytext={"5L0 (LD Five)"} />
        <GeometryCard src={LL0} bodytext={"LL0 (LD Left)"} />
        <GeometryCard src={RL0} bodytext={"RL0 (LD Right)"} />
        <GeometryCard src={TL0} bodytext={"TL0 (LD Top)"} />
        <GeometryCard src={BL0} bodytext={"BL0 (LD Bottom)"} />
        <GeometryCard src={BHW} bodytext={"BHW (CuW/CEE HD Bottom)"} />
        <GeometryCard src={LHW} bodytext={"LHW (CuW/CEE HD Left)"} />
        <GeometryCard src={RHW} bodytext={"RHW (CuW/CEE HD Right)"} />
        <GeometryCard src={THT} bodytext={"THT (Ti/CEH HD Top)"} />
      </div >
    </SimpleFold>
  </>
}

const TapeAdd = ({ showState }) => {
  const { updateTapeList, userList } = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const [, setShow] = showState;
  const form = {
    'batch_id': useState(null),
    'batch_reception_date': useState(new Date()),
    'source': useState(null),
    "receiver": useState(user.username),
    "expiry_date": useState(new Date()),
    "type": useState(null),
    "version": useState(null),
    "delivery_count": useState(null),
    "defective_count": useState(0),
  }
  // eslint-disable-next-line
  const errorForm = {
    "batch_id": useState(""),
    'source': useState(""),
    "type": useState(""),
    "version": useState(""),
    "delivery_count": useState(""),
    "defective_count": useState("")
  };

  const [invalid, setInvalid] = useState("");
  const resetAction = defaultResetAdd(form, { "batch_reception_date": () => new Date(), receiver: user.username, defective_count: 0 })
  const addAction = defaultAdd(form, addTape, updateTapeList, resetAction, setShow);
  useEffect(() => { setInvalid(defaultValidateEdit(errorForm)) }, [errorForm])

  return <TableViewAdd title="Add new Tape" showState={showState} addAction={addAction} invalid={invalid}>
    <HGCFormRowText label="Batch id" dataState={form["batch_id"]} errorState={errorForm["batch_id"]} validate={validateAlphanumeric(true)} autoFocus={true} />
    <HGCFormRowDate label="Batch Reception Date" dataState={form["batch_reception_date"]} />
    <HGCFormRowSelect label="Manufacturer" dataState={form["source"]} errorState={errorForm["source"]} options={tapeSourceOptions} allowNull={false} />
    <HGCFormRowSelect label="Reciever" dataState={form["receiver"]} options={userOptions(userList)} />
    <HGCFormRowDate label="Expiry Date" dataState={form["expiry_date"]} />
    <HGCFormRowSelect label="Geometry Type" dataState={form["type"]} errorState={errorForm["type"]} options={tapeGeometryOptions} allowNull={false} />
    <HGCFormRowText label="Version" dataState={form["version"]} errorState={errorForm["version"]} validate={validatePositiveInt()} />
    <HGCFormRowText label="Delivery count" dataState={form["delivery_count"]} errorState={errorForm["delivery_count"]} validate={validatePositiveInt(true)} />
    <HGCFormRowText label="Defective count" dataState={form["defective_count"]} errorState={errorForm["defective_count"]} validate={validateNonNegativeInt()} />
    <GeometryReference />
  </TableViewAdd>
}

export const TapeList = () => {
  const { tapeList } = useContext(DatabaseContext);
  const [showAdd, setShowAdd] = useState(false);

  return <ProtectedComponent>
    <Button onClick={() => { setShowAdd(true) }}>Add tape order</Button>
    <TapeAdd showState={[showAdd, setShowAdd]} />
    <hr />
    <PaginateTable tableData={tapeList} columnLayout={TapeColumns()} entriesPerPage={10} />
  </ProtectedComponent>
}

export default TapeList;

